import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, ArrowRight } from "lucide-react";
import Seo, { FaqJsonLd, BreadcrumbJsonLd } from "@/components/common/Seo";
import { Reveal } from "@/components/common/Reveal";
import { api } from "@/lib/api";

const SITE = "https://reverenceautos.ca";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setPost(false));
  }, [slug]);

  if (post === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-red" /></div>;
  if (post === false) return <div className="container-rv py-24 text-center"><p className="text-brand-muted">Article not found.</p><Link to="/blog" className="mt-4 inline-block font-semibold text-brand-red">← Back to blog</Link></div>;

  const articleLd = {
    "@context": "https://schema.org", "@type": "Article", headline: post.title,
    description: post.meta_description || post.excerpt, image: post.image,
    author: { "@type": "Organization", name: "Reverence Autos" },
    publisher: { "@type": "Organization", name: "Reverence Autos" },
    datePublished: post.created_at, dateModified: post.updated_at, mainEntityOfPage: `${SITE}/blog/${post.slug}`,
  };

  return (
    <article data-testid="blog-post-page">
      <Seo title={post.meta_title || `${post.title} | Reverence Autos`} description={post.meta_description || post.excerpt}
        path={`/blog/${post.slug}`} image={post.image} jsonLd={articleLd} />
      <FaqJsonLd faq={post.faq} />
      <BreadcrumbJsonLd items={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }]} />

      <div className="relative h-[40vh] min-h-[280px] w-full overflow-hidden">
        <img src={post.image} alt={post.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/90 via-brand-charcoal/40 to-transparent" />
        <div className="container-rv absolute inset-x-0 bottom-0 pb-10">
          <Link to="/blog" className="mb-4 inline-flex items-center gap-2 text-sm text-white/80 hover:text-white"><ArrowLeft className="h-4 w-4" /> All articles</Link>
          <span className="overline block text-brand-red">{post.tags?.[0]} · {post.read_min} min read</span>
          <h1 className="mt-3 max-w-3xl font-serif text-4xl font-bold leading-tight text-white sm:text-5xl">{post.title}</h1>
        </div>
      </div>

      <div className="container-rv py-16 lg:py-20">
        <div className="mx-auto max-w-2xl">
          <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />

          {post.faq?.length > 0 && (
            <div className="mt-14">
              <h2 className="font-serif text-2xl font-bold text-brand-charcoal">Frequently asked questions</h2>
              <div className="mt-6 space-y-6">
                {post.faq.map((f, i) => (
                  <div key={i}>
                    <h3 className="font-serif text-lg font-bold text-brand-charcoal">{f.q}</h3>
                    <p className="mt-2 text-brand-muted leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Reveal className="mt-14 rounded-2xl bg-brand-charcoal p-8 text-center text-white">
            <h3 className="font-serif text-2xl font-bold">Ready for that fresh-car feeling?</h3>
            <p className="mt-2 text-brand-silver">See transparent <Link to="/pricing" className="text-brand-red underline underline-offset-2">pricing</Link> or book your mobile detail today.</p>
            <Link to="/book" className="btn-red mt-6">Book your detail <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </div>
    </article>
  );
}
